(function index(global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.GalleryOnPage = global.NewHomeSource.GalleryOnPage || {};

  class Map {
    /**
     * Creates an instance of the class and initializes the options object.
     *
     * @constructor
     */
    constructor() {
      this.defaultOptions = {
        zoom: 15,
        clickableIcons: false,
        disableDefaultUI: true,
        disableDoubleClickZoom: false,
        minZoom: 5,
        maxZoom: 20,
        panControl: false,
        scrollwheel: true,
        zoomControl: false,
        mapId: 'bdxproducts',
        draggable: true
      };
      this.markers = [];
      this.map = null;
    }

    /**
     * Initializes the map component within the provided root node.
     * Sets up the map container, retrieves location information, and creates an image icon for the map marker.
     *
     * @param {HTMLElement} rootNode - The root DOM node containing the map component and related information.
     */
    initialize(rootNode) {
      this.mapContainer = rootNode.querySelector('[data-component="map-container"]');
      const information = Map.getInformation(rootNode);

      if (!information || !this.mapContainer) {
        return;
      }

      this.geoPosition = {
        lat: +information.latitude,
        lng: +information.longitude
      };
      this.imageIcon = document.createElement('img');
      this.imageIcon.src = information.icon;
      this.redirectUrl = information.redirectUrl;
    }

    /**
     * Handles the event when the Google Maps script has loaded.
     * Removes the event listener and resolves or rejects the promise based on the presence of the Google Maps API.
     *
     * @param {Function} resolve - The function to call if the Google Maps API is available.
     * @param {Function} reject - The function to call if the Google Maps API is not available.
     */
    onScriptLoaded(resolve, reject) {
      document.body.removeEventListener('googleScriptLoadedAcknowledgement', this.onScriptLoaded);
      if (global.google && global.google.maps) {
        resolve(global.google);
      } else {
        reject(null);
      }
    }

    /**
     * Loads the Google script asynchronously by dispatching a custom event and listening for its acknowledgement.
     * Returns a Promise that resolves when the script is loaded, or rejects if loading fails.
     *
     * @returns {Promise<void>} A promise that resolves when the Google script is loaded.
     */
    loadScript() {
      return new Promise((resolve, reject) => {
        this.onScriptLoaded = this.onScriptLoaded.bind(this, resolve, reject);
        document.body.addEventListener('googleScriptLoadedAcknowledgement', this.onScriptLoaded);
        document.body.dispatchEvent(new CustomEvent('askForGoogleScript'));
      });
    }

    /**
     * Asynchronously creates and initializes a Google Map instance with the provided configuration.
     * Merges default options with custom configuration and sets the map center to the current geo position.
     * Loads the Google Maps script if necessary, then creates the map in the specified container.
     * Adds a CSS class to the map container once the map tiles are loaded.
     *
     * @param {Object} [configuration] - Optional custom configuration for the map.
     * @returns {Promise<this>} Resolves with the current instance after the map is created.
     */
    async createMap(configuration) {
      const customConfiguration = configuration || {};
      const options = Object.assign({}, this.defaultOptions, customConfiguration, { center: this.geoPosition });
      await this.loadScript();
      this.map = new global.google.maps.Map(this.mapContainer, options);
      global.google.maps.event.addListenerOnce(this.map, 'tilesloaded', () => {
        this.mapContainer.classList.add('nhs-u-z-index--content');
      });
      return this;
    }

    /**
     * Handles the marker click event by redirecting the browser to the specified URL.
     * @function
     * @returns {void}
     */
    onMarkerClicked() {
      if (this.redirectUrl) {
        global.location.href = this.redirectUrl;
      }
    }

    /**
     * Creates a new AdvancedMarkerElement on the Google Map with the specified configuration.
     *
     * @param {Object} [configuration] - Optional configuration object for the marker.
     * @param {google.maps.LatLng|Object} [configuration.position] - The position of the marker.
     * Defaults to `this.geoPosition`.
     * @param {string} [configuration.title] - The title of the marker. Defaults to an empty string.
     * @returns {this} Returns the current instance for chaining.
     */
    createMarker(configuration) {
      const options = configuration || {};
      const marker = new google.maps.marker.AdvancedMarkerElement({
        map: this.map,
        position: options.position || this.geoPosition,
        title: options.title || '',
        content: this.imageIcon,
        gmpClickable: true
      });
      marker.addListener('gmp-click', this.onMarkerClicked.bind(this));
      this.markers.push(marker);
      return this;
    }

    /**
     * Retrieves and parses JSON information from a child element of the given root node.
     *
     * @param {HTMLElement} rootNode - The root DOM node to search within.
     * @returns {Object|null} The parsed information object, or null if parsing fails or the element is not found.
     */
    static getInformation(rootNode) {
      try {
        const informationElement = rootNode.querySelector('[data-component="map-information"]');
        return JSON.parse(informationElement.textContent);
      } catch (error) {
        return null;
      }
    }
  }

  global.NewHomeSource.GalleryOnPage.Map = Map;
}(window));
